import React from 'react';
import styled from 'styled-components';
import { size } from '../../layout/helpers';
import { theme } from '../../layout/theme';
import Container from '../../Container';
import { CheckIcon } from '../../Icon';
import RouterLink from '../../../Router/Link';
import clsx from 'clsx';

const Inner = styled.div`
  display: flex;
  align-items: center;
`;

const UspList = styled.ul`
  display: flex;
  align-items: center;
  list-style: none;
  padding: 0;
  margin: 0;
`;

const UspItem = styled.li`
  display: flex;
  align-items: center;

  & + & {
    margin-left: ${size(2)};
  }

  ${theme.screens.Md} {
    & + & {
      margin-left: ${size(4.5)};
    }
  }
`;

const ItemIcon = styled.div`
  flex-shrink: 0;
  margin-right: ${size(1)};

  &.is-positive {
    color: ${theme.colors.ui.positive};
  }
`;

const ItemText = styled.div`
  font-size: 0.625rem;
  font-weight: 600;

  ${theme.screens.XS} {
    font-size: 0.75rem;
  }

  ${theme.screens.Md} {
    font-size: 0.875rem;
  }
`;

const ActionList = styled.ul`
  display: flex;
  align-items: center;
  list-style: none;
  padding: 0;
  margin: 0;

  ${theme.screens.LtMd} {
    flex: 1 1 auto;
    justify-content: space-between;
  }

  ${theme.screens.Md} {
    margin-left: auto;
    padding-left: ${size(2)};
  }
`;

const ActionItem = styled.li`
  ${theme.screens.Md} {
    & + & {
      margin-left: ${size(4.5)};
    }
  }
`;

const ActionLink = styled(RouterLink)`
  display: flex;
  align-items: center;
  text-decoration: none;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  text-decoration: none;
`;

const StyledRibbon = styled.div`
  padding: ${size(1.25)} 0;
  background-color: ${theme.colors.bg.alt};
  color: ${theme.colors.text.onColor};

  ${theme.screens.LtMd} {
    padding: ${size(1)} 0;
  }

  ${theme.screens.LtMd} {
    &:not(.checkout) {
      ${UspList} {
        display: none;
      }
    }
  }

  &.checkout {
    ${Inner} {
      justify-content: center;
    }
  }

  ${theme.screens.LtSm} {
    &.checkout {
      display: none;
    }
  }
`;

interface IRibbonAction {
  type: string;
  className?: string;
  icon: React.ReactNode;
  reload?: boolean;
  title: string;
  url: string;
  action?: () => void;
}

interface IRibbon {
  uspList?: string[];
  actionList?: IRibbonAction[];
  checkout?: boolean;
}

const Ribbon = ({ uspList, actionList, checkout }: IRibbon) => {
  if (!uspList && !actionList) {
    return null;
  }

  return (
    <StyledRibbon className={clsx(checkout && 'checkout')}>
      <Container>
        <Inner>
          {uspList && (
            <UspList>
              {uspList?.map((usp, index) => (
                <UspItem key={`${usp}-${index}`}>
                  <ItemIcon className="is-positive">
                    <CheckIcon />
                  </ItemIcon>
                  <ItemText>{usp}</ItemText>
                </UspItem>
              ))}
            </UspList>
          )}
          {actionList && actionList.length > 0 && (
            <ActionList>
              {actionList?.map((action) => (
                <ActionItem key={action.url} className={action.className}>
                  {action?.type === 'action' && action?.action ? (
                    <ActionButton onClick={action?.action}>
                      <ItemIcon>{action.icon}</ItemIcon>
                      <ItemText>{action.title}</ItemText>
                    </ActionButton>
                  ) : (
                    <ActionLink
                      href={action.url}
                      title={action.title}
                      data-reload={action.reload ? true : null}>
                      <ItemIcon>{action.icon}</ItemIcon>
                      <ItemText>{action.title}</ItemText>
                    </ActionLink>
                  )}
                </ActionItem>
              ))}
            </ActionList>
          )}
        </Inner>
      </Container>
    </StyledRibbon>
  );
};

export default Ribbon;
